/**
 * Purchase-needs UI grouping (Suggested Order / Weekly Menu Shopping List).
 *
 * Pure client-side aggregation applied AFTER the backend already
 * returned its raw needs list. The backend response is untouched.
 * Only merges rows that are unambiguously the same ingredient:
 *
 *   1. Same normalised base name (lowercase, punctuation-free,
 *      diacritic-folded, singular/plural collapsed, EN↔FI synonyms
 *      collapsed via the shared dictionary from the recipe importer).
 *   2. Convertible units — grams ↔ kg ↔ mg, ml ↔ cl ↔ dl ↔ l. Merged
 *      rows show the total in the biggest sensible unit ("1 kg"
 *      rather than "1000 g").
 *
 * Rows that normalise to the same base name but carry INCOMPATIBLE
 * units (e.g. g vs ml, or g vs pcs) are kept as separate rows and
 * tagged with `mixed_units: true` so the UI can surface a small
 * warning icon. This is intentional — chefs need to see both entries
 * because we cannot safely combine, say, 500 g of onion and 3 pcs of
 * onion into one shopping row.
 *
 * The dictionary is fetched once via GET /api/dictionary/ingredients
 * and cached. If the fetch fails we fall back to a purely
 * lexical / fuzzy grouping (still catches "Cream" + "cream" duplicates).
 */
import { api } from "@/lib/api";

// ---- Unit taxonomy ---------------------------------------------------
// Each entry says which dimension the unit belongs to and how many
// base-units it represents (base = g for mass, ml for volume, pcs for
// count). Only listed units are convertible; everything else falls
// through as its own bucket.
const UNIT_TABLE = {
    // Mass — base = g
    mg: { dim: "mass", factor: 0.001 },
    g:  { dim: "mass", factor: 1 },
    kg: { dim: "mass", factor: 1000 },
    // Volume — base = ml
    ml: { dim: "vol",  factor: 1 },
    cl: { dim: "vol",  factor: 10 },
    dl: { dim: "vol",  factor: 100 },
    l:  { dim: "vol",  factor: 1000 },
    lt: { dim: "vol",  factor: 1000 },
    // Kitchen spoons — informational but still volume.
    tsp:  { dim: "vol", factor: 5 },
    tbsp: { dim: "vol", factor: 15 },
    // Count — base = pcs
    pcs: { dim: "count", factor: 1 },
    pc:  { dim: "count", factor: 1 },
    piece:  { dim: "count", factor: 1 },
    pieces: { dim: "count", factor: 1 },
    unit:   { dim: "count", factor: 1 },
    units:  { dim: "count", factor: 1 },
    kpl:    { dim: "count", factor: 1 }, // Finnish "kappale"
};

const _normUnit = (u) => String(u || "").trim().toLowerCase();

const unitInfo = (u) => UNIT_TABLE[_normUnit(u)] || null;

/** Convert `qty` in `unit` to the dimension's base unit. Null if the
 * unit is not in the table (e.g. "portion", "bunch", ""). */
export function toBase(qty, unit) {
    const info = unitInfo(unit);
    if (!info) return null;
    return { dim: info.dim, base: Number(qty) * info.factor };
}

/** Format a base-unit total back into a readable string, picking the
 * biggest sensible unit so users see "1.5 kg" not "1500 g". */
export function fromBase(baseValue, dim) {
    const n = Number(baseValue);
    if (!Number.isFinite(n)) return { quantity: 0, unit: "" };
    const round = (v, d) => {
        const p = Math.pow(10, d);
        return Math.round(v * p) / p;
    };
    if (dim === "mass") {
        return n >= 1000
            ? { quantity: round(n / 1000, 3), unit: "kg" }
            : n >= 1
                ? { quantity: round(n, 0), unit: "g" }
                : { quantity: round(n * 1000, 0), unit: "mg" };
    }
    if (dim === "vol") {
        return n >= 1000
            ? { quantity: round(n / 1000, 2), unit: "l" }
            : n >= 100
                ? { quantity: round(n / 100, 2), unit: "dl" }
                : { quantity: round(n, 0), unit: "ml" };
    }
    if (dim === "count") {
        return { quantity: round(n, 2), unit: "pcs" };
    }
    return { quantity: round(n, 2), unit: "" };
}


// ---- Name normalisation --------------------------------------------
const _foldAccents = (s) => {
    if (!s) return "";
    return s.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
};

/** Aggressive lexical normalisation used before dictionary lookup:
 * lowercase, strip accents, drop punctuation, collapse whitespace,
 * best-effort singular/plural collapse (trailing s / es / ies). */
export function normaliseName(name) {
    if (!name) return "";
    let s = _foldAccents(String(name)).toLowerCase();
    s = s.replace(/[^a-z0-9]+/g, " ").trim();
    // Singular/plural: only touch the last token so multi-word names
    // like "cherry tomatoes" collapse to "cherry tomato".
    const tokens = s.split(" ").filter(Boolean).map((tok) => {
        if (tok.length <= 3) return tok;
        if (tok.endsWith("ies") && tok.length > 4) return tok.slice(0, -3) + "y";
        if (tok.endsWith("es") && !/[aeiou]es$/.test(tok)) return tok.slice(0, -2);
        if (tok.endsWith("s") && !tok.endsWith("ss")) return tok.slice(0, -1);
        return tok;
    });
    return tokens.join(" ");
}


// ---- Dictionary -----------------------------------------------------
// Loaded once from GET /api/dictionary/ingredients. Each entry becomes a
// bidirectional set: every synonym in the same bucket normalises to the
// same canonical key (the English head).
let _synonymIndex = null;
let _dictionaryPromise = null;

/** Force-refresh cache — mostly for tests. */
export function _resetDictionaryCache() {
    _synonymIndex = null;
    _dictionaryPromise = null;
}

async function _loadDictionary() {
    if (_synonymIndex) return _synonymIndex;
    if (!_dictionaryPromise) {
        _dictionaryPromise = api.get("/dictionary/ingredients")
            .then(({ data }) => {
                const idx = new Map();
                const dict = data?.dictionary || {};
                // Two passes so single-word heads win: they get first
                // dibs on every synonym, then multi-word phrases fill
                // in the remaining terms without overwriting.
                const singleWord = [];
                const multiWord = [];
                for (const [en, fis] of Object.entries(dict)) {
                    (en.includes(" ") ? multiWord : singleWord)
                        .push([en, fis]);
                }
                const fill = ([en, fis]) => {
                    const canonical = normaliseName(en);
                    const bucket = new Set([canonical, ...fis.map(normaliseName)]);
                    for (const term of bucket) {
                        // First-wins: preserve single-word ownership.
                        if (!idx.has(term)) idx.set(term, canonical);
                    }
                };
                singleWord.forEach(fill);
                multiWord.forEach(fill);
                // Also index every single token of the canonicals so
                // multi-word queries can fall back to the head noun.
                for (const term of Array.from(idx.keys())) {
                    for (const tok of term.split(" ")) {
                        if (tok.length > 3 && !idx.has(tok)) {
                            idx.set(tok, idx.get(term));
                        }
                    }
                }
                _synonymIndex = idx;
                return idx;
            })
            .catch(() => {
                // Fail-open: an empty index makes the grouper fall back
                // to pure lexical matching, which still catches "Cream"
                // + "cream" and "Cream" + "Creams".
                _synonymIndex = new Map();
                return _synonymIndex;
            });
    }
    return _dictionaryPromise;
}

/** Look up the canonical key for a normalised name. Falls back to the
 * name itself when no dictionary match.
 *
 * Two-step lookup so multi-word variants collapse onto their head
 * noun. Example: "cooking cream" and "cream" both resolve to the
 * canonical "cream" as long as the dictionary knows the head token.
 */
function _canonical(norm, index) {
    if (!norm) return "";
    const direct = index.has(norm) ? index.get(norm) : norm;
    // Also try the tokens — from longest suffix down to single tokens.
    // The head noun (last token) usually carries the identity, so we
    // prefer the shorter canonical when a token match exists.
    const tokens = norm.split(" ");
    for (let i = tokens.length - 1; i >= 0; i--) {
        const tok = tokens[i];
        if (tok.length > 3 && index.has(tok)) {
            const tokCanonical = index.get(tok);
            // Prefer the shorter canonical only when it isn't itself a
            // multi-word phrase — this avoids "onion" → "spring onion"
            // via aliasing on the token "onion".
            if (tokCanonical.length < direct.length
                    && !tokCanonical.includes(" ")) {
                return tokCanonical;
            }
        }
    }
    return direct;
}


// ---- Grouper --------------------------------------------------------

/** Sum a base value from a row into the accumulator. Also updates the
 * list of source rows so the UI can render tooltips like "combined from:
 * cream + kerma". */
function _accumulate(bucket, row, base) {
    bucket.base_total += base.base;
    bucket.dim = base.dim;
    bucket.sources.push({
        ingredient_name: row.ingredient_name,
        quantity: row.total_quantity ?? row.quantity ?? 0,
        unit: row.unit,
        suggested_order: row.suggested_order ?? 0,
        available: row.available ?? 0,
    });
    // Aggregate stock signals — worst status wins (out_of_stock > low_stock > ready).
    const rank = { out_of_stock: 3, low_stock: 2, unknown: 1, ready: 0 };
    if ((rank[row.stock_status] ?? -1) > (rank[bucket.stock_status] ?? -1)) {
        bucket.stock_status = row.stock_status;
    }
    // Suggested order + available — sum in base units too.
    if (Number.isFinite(Number(row.suggested_order))) {
        bucket.suggested_base += Number(row.suggested_order) * (unitInfo(row.unit)?.factor || 1);
    }
    if (Number.isFinite(Number(row.available))) {
        bucket.avail_base += Number(row.available) * (unitInfo(row.unit)?.factor || 1);
    }
}


/** Group a raw purchase-needs array into deduplicated rows.
 *
 * Return shape (each element):
 *  {
 *    ingredient_name    // display label — canonical key or first source
 *    total_quantity     // in the display unit
 *    unit               // display unit (kg/l/pcs/…)
 *    available          // aggregated in display unit
 *    suggested_order    // aggregated in display unit
 *    stock_status       // worst of the group
 *    _sources           // array of the merged raw rows (for tooltip)
 *    _mixed_units       // true iff any sibling row was kept separate
 *                       // because its unit dimension didn't match
 *  }
 *
 * Rows we can't safely merge (unknown or incompatible units) are
 * emitted as-is with `_mixed_units=true` when another row shares the
 * same canonical name.
 */
/** Group a raw purchase-needs array into deduplicated rows.
 *
 * When the backend returns rows already carrying `_already_grouped: true`
 * (v5 multilingual recognition path), we trust the server-side grouping
 * verbatim — the client never regroups incompatible requirements or
 * double-sums shared stock.
 */
export async function groupPurchaseNeeds(rawNeeds) {
    const rows = Array.isArray(rawNeeds) ? rawNeeds : [];
    if (rows.length === 0) return [];
    // v5: server-side already grouped. Pass-through, keeping the
    // display sources + chef popover fields intact.
    if (rows.every((r) => r && r._already_grouped)) {
        return rows.map((r) => ({
            ...r,
            // Legacy sibling fields the UI uses for the merge chip
            _sources: r._sources || [],
            _mixed_units: false,
        }));
    }
    const index = await _loadDictionary();

    // Two-pass:
    //   1) Compute canonical key for every row.
    //   2) Bucket by (key, dim). Any key with more than one dim gets
    //      `mixed_units=true` on all its rows.
    const withKey = rows.map((r) => {
        const norm = normaliseName(r.ingredient_name);
        const key = _canonical(norm, index);
        const info = unitInfo(r.unit);
        return { r, norm, key, dim: info?.dim || null };
    });

    // Track which canonical keys carry more than one dimension.
    const dimsPerKey = new Map();
    for (const wk of withKey) {
        const s = dimsPerKey.get(wk.key) || new Set();
        s.add(wk.dim || "unknown");
        dimsPerKey.set(wk.key, s);
    }

    // Bucket rows.
    const buckets = new Map();     // "key|dim" → aggregated row
    const singletons = [];         // rows we can't merge (unknown dim)

    for (const wk of withKey) {
        const { r, key, dim } = wk;
        const mixed = (dimsPerKey.get(key) || new Set()).size > 1;
        if (!dim) {
            // Unit is not in our table — never fold, keep as-is.
            singletons.push({
                ...r,
                _sources: [
                    { ingredient_name: r.ingredient_name,
                      quantity: r.total_quantity ?? 0,
                      unit: r.unit,
                      suggested_order: r.suggested_order ?? 0,
                      available: r.available ?? 0 },
                ],
                _mixed_units: mixed,
                _canonical: key,
            });
            continue;
        }
        const bkey = `${key}|${dim}`;
        if (!buckets.has(bkey)) {
            buckets.set(bkey, {
                _canonical: key,
                _dim: dim,
                _mixed_units: mixed,
                base_total: 0,
                suggested_base: 0,
                avail_base: 0,
                dim,
                stock_status: r.stock_status || "unknown",
                sources: [],
            });
        }
        const bucket = buckets.get(bkey);
        const base = toBase(r.total_quantity, r.unit);
        if (base) _accumulate(bucket, r, base);
    }

    // Emit merged rows in a stable order (canonical, then dim).
    const merged = [];
    for (const bucket of buckets.values()) {
        const total = fromBase(bucket.base_total, bucket.dim);
        const supp  = fromBase(bucket.suggested_base, bucket.dim);
        const avail = fromBase(bucket.avail_base, bucket.dim);
        const displayName = _pickDisplayName(bucket.sources);
        merged.push({
            ingredient_name: displayName,
            total_quantity: total.quantity,
            unit: total.unit,
            available: avail.quantity,
            suggested_order: supp.quantity,
            stock_status: bucket.stock_status,
            _sources: bucket.sources,
            _mixed_units: bucket._mixed_units,
            _canonical: bucket._canonical,
        });
    }
    // Append the ungroupable singletons at the end.
    const out = [...merged, ...singletons];
    out.sort((a, b) => (b._sources?.length || 1) - (a._sources?.length || 1)
                  || String(a.ingredient_name).localeCompare(b.ingredient_name));
    return out;
}


/** Choose the label to show for a merged bucket. Rule of thumb: pick
 * the shortest source name in Title Case — this favours "Cream" over
 * "Lactose-free cooking cream" while remaining honest about what
 * survived normalisation. */
function _pickDisplayName(sources) {
    if (!sources || sources.length === 0) return "";
    const uniqNames = Array.from(new Set(sources.map((s) => s.ingredient_name)))
        .filter(Boolean);
    if (uniqNames.length === 0) return "";
    uniqNames.sort((a, b) => a.length - b.length || a.localeCompare(b));
    return uniqNames[0];
}
